/**
 * <s-cursor> — custom pointer for clickable elements (layout/theme.liquid, styles: ".s-cursor" in config/styles.css).
 *
 * Mouse/trackpad only. Over links, buttons and other controls a round ink-navy ball with a white arrow springs in
 * and trails the pointer with a light ease; the native cursor is hidden only while the ball is showing
 * (html[data-cursor-on]). External links (target="_blank" or another host) tilt the arrow to ↗, pressing squeezes
 * the ball, and on dark surfaces the ball turns white with a navy arrow. Text fields and disabled controls keep
 * their native cursor. With reduced motion the ball follows the pointer exactly, without trailing or bounce.
 */
const CLICKABLE = [
  'a[href]',
  'button:not(:disabled)',
  '[role="button"]:not([aria-disabled="true"])',
  '[role="tab"]',
  '[role="link"]',
  'summary',
  'label[for]',
  'select',
  'input[type="submit"]:not(:disabled)',
  'input[type="button"]:not(:disabled)',
  'input[type="checkbox"]:not(:disabled)',
  'input[type="radio"]:not(:disabled)',
  '[data-mobile-nav-push]',
  '[data-promo-pill]',
].join(',');
const DARK = '.bg-on-surface, .bg-ink, .top-bar, .promo-popup, .bg-\\[\\#14122B\\], [data-cursor-dark]';
const EASE = 0.24;

class SCursor extends HTMLElement {
  #ac;
  #frame = 0;
  #x = -100;
  #y = -100;
  #tx = -100;
  #ty = -100;
  #target = null;
  #reduced = false;

  connectedCallback() {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    this.#reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal, passive: true };
    document.addEventListener('pointermove', this.#onMove, opts);
    document.addEventListener('pointerdown', () => this.toggleAttribute('data-down', true), opts);
    document.addEventListener('pointerup', () => this.removeAttribute('data-down'), opts);
    document.documentElement.addEventListener('pointerleave', () => this.#setTarget(null), opts);
    window.addEventListener('blur', () => this.#setTarget(null), opts);
    document.documentElement.setAttribute('data-cursor-ready', '');
  }

  disconnectedCallback() {
    this.#ac?.abort();
    cancelAnimationFrame(this.#frame);
    document.documentElement.removeAttribute('data-cursor-ready');
    document.documentElement.removeAttribute('data-cursor-on');
  }

  /** @param {PointerEvent} event */
  #onMove = (event) => {
    if (event.pointerType && event.pointerType !== 'mouse') return;
    this.#tx = event.clientX;
    this.#ty = event.clientY;
    let el = event.target instanceof Element ? event.target.closest(CLICKABLE) : null;
    // Open dialogs sit in the browser's top layer above the ball, so they keep the native cursor.
    if (el?.closest('dialog')) el = null;
    this.#setTarget(el);
    if (!this.#target) return;
    if (!this.hasAttribute('data-active') || this.#reduced) {
      this.#x = this.#tx;
      this.#y = this.#ty;
    }
    this.#loop();
  };

  /** @param {Element | null} el */
  #setTarget(el) {
    if (el === this.#target) return;
    this.#target = el;
    const root = document.documentElement;
    if (!el) {
      this.removeAttribute('data-active');
      root.removeAttribute('data-cursor-on');
      return;
    }
    this.toggleAttribute('data-dark', Boolean(el.closest(DARK)));
    this.toggleAttribute('data-external', this.#isExternal(el));
    // Restart the arrival ring each time the ball lands on a new element.
    this.removeAttribute('data-active');
    void this.offsetWidth;
    this.setAttribute('data-active', '');
    root.setAttribute('data-cursor-on', '');
  }

  /** @param {Element} el */
  #isExternal(el) {
    if (!(el instanceof HTMLAnchorElement)) return false;
    if (el.target === '_blank') return true;
    try {
      const url = new URL(el.href, window.location.href);
      return /^https?:$/.test(url.protocol) && url.host !== window.location.host;
    } catch {
      return false;
    }
  }

  #loop() {
    if (this.#frame) return;
    const step = () => {
      const k = this.#reduced ? 1 : EASE;
      this.#x += (this.#tx - this.#x) * k;
      this.#y += (this.#ty - this.#y) * k;
      this.style.translate = `${this.#x}px ${this.#y}px`;
      const settled = Math.abs(this.#tx - this.#x) < 0.1 && Math.abs(this.#ty - this.#y) < 0.1;
      this.#frame = settled ? 0 : requestAnimationFrame(step);
    };
    this.#frame = requestAnimationFrame(step);
  }
}

if (!customElements.get('s-cursor')) customElements.define('s-cursor', SCursor);
